<template>
  <div>
    <div
      v-if="showSectionAddress"
      class="db-tw-mb-9"
    >
      <DbInput
        :modelValue="postalAddress"
        :inputAttrs="{ required: isMandatoryAddress }"
        v-on:update:model-value="$emit('update:postalAddress', $event)"
      >
        {{ labelPostalAddress }}
        <template
          v-if="v$.fieldAddress.$error"
          v-slot:error
        >
          <DbRfpErrors :errors="v$.fieldAddress.$errors" />
        </template>
      </DbInput>
    </div>
    <div
      v-if="showSectionAddress"
      class="db-tw-grid md:db-tw-grid-cols-2 md:db-tw-gap-3 db-tw-mb-9"
    >
      <div class="db-tw-mb-10 md:db-tw-mb-0">
        <DbInput
          :modelValue="postalCityRegion"
          :inputAttrs="{ required: isMandatoryCityRegion }"
          v-on:update:model-value="$emit('update:postalCityRegion', $event)"
        >
          {{ labelPostalCityRegion }}
          <template
            v-if="v$.fieldCityRegion.$error"
            v-slot:error
          >
            <DbRfpErrors :errors="v$.fieldCityRegion.$errors" />
          </template>
        </DbInput>
      </div>
      <div>
        <DbInput
          :modelValue="postalState"
          :inputAttrs="{ required: isMandatoryState }"
          v-on:update:model-value="$emit('update:postalState', $event)"
        >
          {{ labelPostalState }}
          <template
            v-if="v$.fieldState.$error"
            v-slot:error
          >
            <DbRfpErrors :errors="v$.fieldState.$errors" />
          </template>
        </DbInput>
      </div>
    </div>
    <div
      v-if="showSectionZipCode || showSectionCountry"
      class="db-tw-grid db-tw-mb-9"
      :class="{ 'md:db-tw-grid-cols-2 md:db-tw-gap-3': showSectionZipCode && showSectionCountry }"
    >
      <div
        v-if="showSectionZipCode"
        class="db-tw-mb-10 md:db-tw-mb-0"
      >
        <DbInput
          :modelValue="postalZipCode"
          :inputAttrs="{ required: isMandatoryZipCode }"
          v-on:update:model-value="$emit('update:postalZipCode', $event)"
        >
          {{ labelPostalZipCode }}
          <template
            v-if="v$.fieldZipCode.$error"
            v-slot:error
          >
            <DbRfpErrors :errors="v$.fieldZipCode.$errors" />
          </template>
        </DbInput>
      </div>
      <div v-if="showSectionCountry">
        <DbSelectCustom
          v-if="options.length"
          :modelValue="postalCountry"
          :options="options"
          :inputAttrs="{ required: isMandatoryCountryCode }"
          v-on:update:model-value="$emit('update:postalCountry', $event)"
        >
          {{ labelPostalCountryCode }}
          <template
            v-if="v$.fieldCountry.$error"
            v-slot:error
          >
            <DbRfpErrors :errors="v$.fieldCountry.$errors" />
          </template>
        </DbSelectCustom>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { requiredIf, maxLength } from '@/utils/i18n-validators'
import { countries } from './CountryList'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    postalAddress?: string
    postalCityRegion?: string
    postalZipCode?: string
    postalState?: string
    postalCountry?: string
    labelPostalAddress: string
    labelPostalCityRegion: string
    labelPostalZipCode: string
    labelPostalState: string
    labelPostalCountryCode: string
    isMandatoryAddress: boolean
    isMandatoryCityRegion: boolean
    isMandatoryState: boolean
    isMandatoryZipCode: boolean
    isMandatoryCountryCode: boolean
    /**
     * Toggle section Address.
     *
     * This does not control the mandatory states, we expect
     * the other props to be adapted accordingly
     * */
    showSectionAddress?: boolean
    showSectionZipCode?: boolean
    showSectionCountry?: boolean
  }>(),
  {
    postalAddress: '',
    postalCityRegion: '',
    postalZipCode: '',
    postalState: '',
    postalCountry: '',
    showSectionAddress: true,
    showSectionZipCode: true,
    showSectionCountry: true,
  },
)

defineEmits<{
  (e: 'update:postalAddress', postalAddress: string): void
  (e: 'update:postalCityRegion', postalCityRegion: string): void
  (e: 'update:postalState', postalState: string): void
  (e: 'update:postalZipCode', postalZipCode: string): void
  (e: 'update:postalCountry', postalCountry: string): void
}>()

/* Format select country options */
const { t } = useI18n()
const options = computed(() => {
  const defaultValue = {
    label: t('Select country'),
    value: '',
  }

  return [
    defaultValue,
    ...countries.map((item) => {
      return {
        label: item.display_name,
        value: item.code,
      }
    }),
  ]
})

const state = computed(() => {
  return {
    fieldAddress: props.postalAddress,
    fieldCityRegion: props.postalCityRegion,
    fieldState: props.postalState,
    fieldZipCode: props.postalZipCode,
    fieldCountry: props.postalCountry,
  }
})

const rules = computed(() => {
  return {
    fieldAddress: { required: requiredIf(props.isMandatoryAddress), maxLengthValue: maxLength(75) },
    fieldCityRegion: {
      required: requiredIf(props.isMandatoryCityRegion),
      maxLengthValue: maxLength(40),
    },
    fieldState: { required: requiredIf(props.isMandatoryState), maxLengthValue: maxLength(40) },
    fieldZipCode: { required: requiredIf(props.isMandatoryZipCode), maxLengthValue: maxLength(40) },
    fieldCountry: { required: requiredIf(props.isMandatoryCountryCode) },
  }
})

const v$ = useVuelidate(rules, state, { $autoDirty: true })
</script>
